import { Injectable, Inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ForceCacheClearService } from './force-cache-clear.service';

@Injectable({
  providedIn: 'root'
})
export class VersionCheckService {
  private readonly APP_VERSION = '5.6.16'; // Actualizar con cada despliegue
  private readonly VERSION_STORAGE_KEY = 'app_version';
  private readonly LAST_VERSION_CHECK_KEY = 'last_version_check';

  constructor(
    @Inject(PLATFORM_ID) private platformId: Object,
    private forceCacheClearService: ForceCacheClearService
  ) {}

  /**
   * Inicia la verificación de versión solo en el startup
   */
  startVersionCheck(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    // Solo verificar al iniciar la aplicación
    this.checkVersionOnStartup();
  }

  /**
   * Verifica la versión al iniciar la aplicación
   */
  private checkVersionOnStartup(): void {
    const storedVersion = localStorage.getItem(this.VERSION_STORAGE_KEY);
    
    if (!storedVersion) {
      // Primera vez que se carga la app
      localStorage.setItem(this.VERSION_STORAGE_KEY, this.APP_VERSION);
      console.info(`Primera instalación detectada - Versión: ${this.APP_VERSION}`);
      return;
    }

    if (storedVersion !== this.APP_VERSION) {
      console.info(`Nueva versión detectada: ${storedVersion} -> ${this.APP_VERSION}`);
      this.handleVersionUpdate();
    } else {
      console.info(`Versión actual: ${this.APP_VERSION} - No se requiere limpieza`);
    }
  }

  /**
   * Maneja la actualización de versión limpiando cache y cookies
   */
  private handleVersionUpdate(): void {
    console.info('Iniciando limpieza de cache y cookies para nueva versión...');
    
    try {
      // Mostrar notificación antes de limpiar
      this.showUpdateNotification();
      
      // Limpiar localStorage (mantener solo datos esenciales)
      this.clearLocalStorage();
      
      // Limpiar sessionStorage
      this.clearSessionStorage();
      
      // Limpiar cookies de la aplicación
      this.clearApplicationCookies();
      
      // Actualizar la versión almacenada
      localStorage.setItem(this.VERSION_STORAGE_KEY, this.APP_VERSION);
      
      console.info('Cache y cookies limpiados exitosamente');
      
      // Limpiar cache del navegador (esto incluye el reload)
      this.clearBrowserCache();
      
    } catch (error) {
      console.error('Error al limpiar cache y cookies:', error);
      // Si falla la limpieza automática, al menos forzar recarga
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    }
  }

  /**
   * Limpia localStorage manteniendo datos esenciales
   */
  private clearLocalStorage(): void {
    const essentialKeys = [
      this.VERSION_STORAGE_KEY,
      this.LAST_VERSION_CHECK_KEY,
      'user_language',
      'user_preferences'
    ];

    const keysToKeep: {[key: string]: string} = {};
    
    // Guardar datos esenciales
    essentialKeys.forEach(key => {
      const value = localStorage.getItem(key);
      if (value) {
        keysToKeep[key] = value;
      }
    });

    // Limpiar todo localStorage
    localStorage.clear();

    // Restaurar datos esenciales
    Object.entries(keysToKeep).forEach(([key, value]) => {
      localStorage.setItem(key, value);
    });
  }

  /**
   * Limpia sessionStorage
   */
  private clearSessionStorage(): void {
    sessionStorage.clear();
  }

  /**
   * Limpia cookies de la aplicación
   */
  private clearApplicationCookies(): void {
    const cookies = document.cookie.split(';');
    
    cookies.forEach(cookie => {
      const eqPos = cookie.indexOf('=');
      const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();
      
      // No eliminar cookies esenciales del sistema
      const essentialCookies = ['_ga', '_gid', '_gat', 'consent'];
      if (!essentialCookies.some(essential => name.startsWith(essential))) {
        this.deleteCookie(name);
      }
    });
  }

  /**
   * Elimina una cookie específica
   */
  private deleteCookie(name: string): void {
    const domain = window.location.hostname;
    const path = '/';
    
    // Eliminar cookie con diferentes combinaciones de dominio y path
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path};`;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; domain=${domain};`;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=${path}; domain=.${domain};`;
  }

  /**
   * Intenta limpiar el cache del navegador
   */
  private clearBrowserCache(): void {
    // Forzar recarga sin cache en la próxima navegación
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(registrations => {
        registrations.forEach(registration => {
          registration.update();
        });
      });
    }

    // Limpiar cache de imágenes y recursos estáticos
    if ('caches' in window) {
      caches.keys().then(cacheNames => {
        cacheNames.forEach(cacheName => {
          caches.delete(cacheName);
        });
      });
    }

    // Forzar recarga completa de la página después de limpiar
    setTimeout(() => {
      window.location.reload();
    }, 1000);
  }

  /**
   * Muestra una notificación al usuario sobre la actualización
   */
  private showUpdateNotification(): void {
    // Crear una notificación discreta
    const notification = document.createElement('div');
    notification.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      background: #4CAF50;
      color: white;
      padding: 12px 16px;
      border-radius: 4px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.2);
      z-index: 10000;
      font-family: Arial, sans-serif;
      font-size: 14px;
      max-width: 300px;
      opacity: 0;
      transition: opacity 0.3s ease;
    `;
    notification.textContent = 'Aplicación actualizada - Cache limpiado';
    
    document.body.appendChild(notification);
    
    // Animar entrada
    setTimeout(() => {
      notification.style.opacity = '1';
    }, 100);
    
    // Remover después de 3 segundos
    setTimeout(() => {
      notification.style.opacity = '0';
      setTimeout(() => {
        document.body.removeChild(notification);
      }, 300);
    }, 3000);
  }

  /**
   * Fuerza una verificación inmediata de versión (solo local)
   */
  forceVersionCheck(): void {
    console.info('Forzando verificación de versión...');
    this.checkVersionOnStartup();
  }

  /**
   * Obtiene la versión actual de la aplicación
   */
  getCurrentVersion(): string {
    return this.APP_VERSION;
  }

  /**
   * Ejecuta una limpieza forzada de emergencia
   * Usar solo cuando la limpieza automática falla
   */
  emergencyCleanup(): void {
    console.info('Ejecutando limpieza de emergencia...');
    this.forceCacheClearService.emergencyClear();
  }

  /**
   * Expone el servicio de limpieza forzada para casos críticos
   */
  getForceClearService(): ForceCacheClearService {
    return this.forceCacheClearService;
  }
}
